/**
 * A tab strip with its panel, the open tab kept in the URL (`?tab=` unless
 * `param` says otherwise) so a tab links and survives a reload. The strip is
 * the house `.tabs` bar (`card` puts it edge to edge in a `.list-card`). A tab
 * the caller may not see is not rendered at all, and an unknown or hidden
 * `?tab=` value falls back to the first tab shown. Only the open panel is in
 * the page. Left and Right (and Home, End) move between tabs and open them.
 * A click adds a history entry (Back returns to the previous tab); arrow keys
 * replace it, so walking along the strip does not fill the history.
 */

import { useId, useRef, type KeyboardEvent, type ReactNode } from 'react';
import { useSearchParams } from 'react-router-dom';

export interface TabItem {
  key: string;
  label: string;
  icon?: ReactNode;
  /** A count bubble after the label. */
  count?: number;
  /** True when the caller may not see the tab: it is left out. */
  hidden?: boolean;
}

interface TabsProps {
  tabs: TabItem[];
  /** The strip's accessible name. */
  label: string;
  /** The URL query parameter that keeps the open tab. */
  param?: string;
  /** Edge-to-edge bar for a `.list-card`. */
  card?: boolean;
  /** Whether a click adds a history entry (default) or replaces the current one. Arrow keys always replace. */
  history?: 'push' | 'replace';
  /** Query parameters that belong to the tab being left (a section open in it) and are dropped when another tab opens. */
  resets?: string[];
  /** Beside the strip, on its line (a screen's actions). */
  trailing?: ReactNode;
  /** The open tab's body. */
  children: (key: string) => ReactNode;
}

/** The tab an arrow, Home or End key moves to, or null for any other key. */
export function nextIndex(key: string, index: number, total: number): number | null {
  switch (key) {
    case 'ArrowRight':
      return (index + 1) % total;
    case 'ArrowLeft':
      return (index - 1 + total) % total;
    case 'Home':
      return 0;
    case 'End':
      return total - 1;
    default:
      return null;
  }
}

export function Tabs({ tabs, label, param = 'tab', card = false, history = 'push', resets = [], trailing, children }: TabsProps) {
  const [params, setParams] = useSearchParams();
  const base = useId();
  const strip = useRef<HTMLDivElement>(null);
  const shown = tabs.filter((tab) => !tab.hidden);
  const asked = params.get(param);
  const active = shown.find((tab) => tab.key === asked) ?? shown[0];
  if (!active) return null;
  const tabId = (key: string) => `${base}-tab-${key}`;
  const panelId = (key: string) => `${base}-panel-${key}`;

  const open = (key: string, mode: 'push' | 'replace') => {
    setParams(
      (previous) => {
        const next = new URLSearchParams(previous);
        next.set(param, key);
        for (const name of resets) next.delete(name);
        return next;
      },
      { replace: mode === 'replace' },
    );
  };

  const onKeyDown = (event: KeyboardEvent<HTMLButtonElement>, index: number) => {
    if (event.altKey || event.ctrlKey || event.metaKey) return;
    const target = nextIndex(event.key, index, shown.length);
    if (target === null) return;
    event.preventDefault();
    open(shown[target]?.key ?? active.key, 'replace');
    strip.current?.querySelectorAll<HTMLButtonElement>('[role="tab"]')[target]?.focus();
  };

  const bar = (
      <div ref={strip} className={`tabs${card ? ' card-tabs' : ''}`} role="tablist" aria-label={label}>
        {shown.map((tab, index) => {
          const selected = tab.key === active.key;
          return (
            <button
              key={tab.key}
              type="button"
              role="tab"
              id={tabId(tab.key)}
              aria-selected={selected}
              aria-controls={selected ? panelId(tab.key) : undefined}
              tabIndex={selected ? 0 : -1}
              className={`tab${selected ? ' is-active' : ''}`}
              onClick={() => open(tab.key, history)}
              onKeyDown={(event) => onKeyDown(event, index)}
            >
              {tab.icon}
              {tab.label}
              {tab.count !== undefined && tab.count > 0 && <span className="count"><span className="sr-only">, </span>{tab.count}</span>}
            </button>
          );
        })}
      </div>
  );

  return (
    <>
      {trailing ? <div className="tabs-with-trailing">{bar}{trailing}</div> : bar}
      <div role="tabpanel" id={panelId(active.key)} aria-labelledby={tabId(active.key)} tabIndex={0}>
        {children(active.key)}
      </div>
    </>
  );
}
